/**
 * Lens selector (Phase 4). Lists the bundled lenses (design:listLenses) as
 * toggles; the selected ids feed the co-pilot system prompt, Hammer, and the
 * design review (TDD §10). Controlled — the host owns selection state.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import type { Lens } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../../transport/context.js';
import { listLenses } from './anvil-api';

export interface LensSelection {
  id: string;
  kind: 'agent' | 'skill';
}

export interface LensPanelProps {
  selected: LensSelection[];
  onChange: (next: LensSelection[]) => void;
}

export function LensPanel({ selected, onChange }: LensPanelProps): ReactElement {
  const client = useCrucibleClient();
  const [lenses, setLenses] = useState<Lens[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listLenses(client)
      .then((l) => {
        if (!cancelled) setLenses(l);
      })
      .catch(() => {
        /* lenses optional */
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const isSelected = (id: string): boolean => selected.some((s) => s.id === id);
  const toggle = (lens: Lens): void => {
    onChange(
      isSelected(lens.id)
        ? selected.filter((s) => s.id !== lens.id)
        : [...selected, { id: lens.id, kind: lens.kind }],
    );
  };

  if (lenses.length === 0) return <></>;

  return (
    <div className="relative" data-testid="design-lens-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-crucible-border px-2 py-0.5 text-xs text-crucible-muted hover:bg-crucible-bg-elevated"
      >
        Lenses{selected.length > 0 ? ` (${selected.length})` : ''}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-64 rounded border border-crucible-border bg-crucible-surface p-2 shadow-xl">
          <p className="mb-1 text-[10px] uppercase tracking-wide text-crucible-muted">
            Apply specialist perspectives
          </p>
          <ul className="max-h-64 space-y-0.5 overflow-y-auto">
            {lenses.map((lens) => (
              <li key={lens.id}>
                <label className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm hover:bg-crucible-bg-elevated">
                  <input
                    type="checkbox"
                    checked={isSelected(lens.id)}
                    onChange={() => toggle(lens)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-crucible-fg">{lens.name}</span>
                    <span className="ml-1 text-[10px] uppercase opacity-50">{lens.kind}</span>
                    {lens.description && (
                      <span className="block text-[11px] text-crucible-muted">
                        {lens.description}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
